/**
 * Helpers WebAuthn (Auth v2) — base64url + PublicKeyCredential options.
 * Filtra credenciales legacy `cred-*` que rompen atob/WebAuthn nativo.
 */

function supportsWebAuthn() {
  return (
    typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && typeof navigator !== 'undefined'
    && navigator.credentials
    && typeof navigator.credentials.create === 'function'
    && typeof navigator.credentials.get === 'function'
  );
}

function bufferToBase64Url(buffer) {
  const bytes = buffer instanceof ArrayBuffer
    ? new Uint8Array(buffer)
    : buffer instanceof Uint8Array
      ? buffer
      : new Uint8Array(buffer || []);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBuffer(value) {
  const raw = String(value || '').trim().replace(/-/g, '+').replace(/_/g, '/');
  const pad = raw.length % 4 === 0 ? '' : '='.repeat(4 - (raw.length % 4));
  const binary = atob(raw + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function isLegacyCredentialId(id) {
  return String(id || '').startsWith('cred-');
}

function mapCredentialDescriptor(desc) {
  if (!desc || typeof desc !== 'object') return null;
  const idRaw = desc.id;
  if (idRaw == null) return null;
  if (typeof idRaw === 'string' && isLegacyCredentialId(idRaw)) return null;
  return {
    ...desc,
    id: typeof idRaw === 'string' ? base64UrlToBuffer(idRaw) : idRaw,
  };
}

export function toGetOptions(publicKey) {
  const pk = publicKey && typeof publicKey === 'object' ? publicKey : {};
  const allowCredentials = Array.isArray(pk.allowCredentials)
    ? pk.allowCredentials.map(mapCredentialDescriptor).filter(Boolean)
    : undefined;

  return {
    ...pk,
    challenge: typeof pk.challenge === 'string' ? base64UrlToBuffer(pk.challenge) : pk.challenge,
    allowCredentials,
  };
}

export function toCreateOptions(publicKey) {
  const pk = publicKey && typeof publicKey === 'object' ? publicKey : {};
  const excludeCredentials = Array.isArray(pk.excludeCredentials)
    ? pk.excludeCredentials.map(mapCredentialDescriptor).filter(Boolean)
    : undefined;

  return {
    ...pk,
    challenge: typeof pk.challenge === 'string' ? base64UrlToBuffer(pk.challenge) : pk.challenge,
    user: pk.user
      ? {
        ...pk.user,
        id: typeof pk.user.id === 'string' ? base64UrlToBuffer(pk.user.id) : pk.user.id,
      }
      : pk.user,
    excludeCredentials,
  };
}

export function attestationToVerifyPayload(cred, challengeId) {
  const response = cred.response;
  return {
    challengeId,
    credentialId: bufferToBase64Url(cred.rawId),
    publicKey: bufferToBase64Url(response.getPublicKey()),
    clientDataJSON: bufferToBase64Url(response.clientDataJSON),
    authenticatorData: bufferToBase64Url(response.getAuthenticatorData()),
    attestationObject: bufferToBase64Url(response.attestationObject),
    algorithm: 'ES256',
    transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
  };
}

export function assertionToVerifyPayload(assertion, challengeId) {
  const response = assertion.response;
  return {
    challengeId,
    credentialId: bufferToBase64Url(assertion.rawId),
    signature: bufferToBase64Url(response.signature),
    authenticatorData: bufferToBase64Url(response.authenticatorData),
    clientDataJSON: bufferToBase64Url(response.clientDataJSON),
  };
}

/** Códigos de webauthn-runtime al cancelar / descartar el prompt OS. */
const WEBAUTHN_USER_CANCEL_CODES = new Set([
  'WEBAUTHN_NOT_ALLOWED',
  'WEBAUTHN_CANCELLED_OR_TIMEOUT',
]);

/**
 * Cancelación / dismiss del prompt OS (Face ID, huella, AbortSignal).
 * Incluye WebAuthnRuntimeError (name distinto; mirar code / cause).
 */
export function isWebAuthnUserCancelled(err) {
  if (!err) return false;
  const name = err.name || '';
  const code = err.code || '';
  const causeName = err.cause?.name || '';
  if (WEBAUTHN_USER_CANCEL_CODES.has(code)) return true;
  if (name === 'NotAllowedError' || name === 'AbortError') return true;
  if (causeName === 'NotAllowedError' || causeName === 'AbortError') return true;
  const message = String(err.message || '');
  return /timed out|not allowed/i.test(message);
}

/**
 * @returns {string|null} Mensaje para UI, o null si el usuario canceló (sin error rojo).
 */
export function mapWebAuthnError(
  err,
  fallback = 'No se pudo verificar tu acceso biométrico. Ingresá con usuario y contraseña.',
) {
  if (isWebAuthnUserCancelled(err)) return null;
  const name = err?.name || '';
  const code = err?.code || '';
  const causeName = err?.cause?.name || '';
  if (
    name === 'NotSupportedError'
    || code === 'WEBAUTHN_NOT_SUPPORTED'
    || causeName === 'NotSupportedError'
  ) {
    return 'Este navegador o dispositivo no soporta WebAuthn.';
  }
  return fallback;
}

export {
  supportsWebAuthn,
  bufferToBase64Url,
  base64UrlToBuffer,
  isLegacyCredentialId,
};
